import { useState, type ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Mail, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '@/services/authService';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/store/language-store';
import { useTheme } from '@/components/ThemeProvider';
import { cn } from '@/lib/utils';
import LogoDark from '../assets/logo-dark.svg?url';
import LogoLight from '../assets/logo-light.svg?url';
import { SocialLoginButton } from '../components/auth/SocialLoginButton';

// Password validation criteria
const PASSWORD_MIN_LENGTH = 8;
const HAS_UPPERCASE = /[A-Z]/;
const HAS_LOWERCASE = /[a-z]/;
const HAS_NUMBER = /[0-9]/;
const HAS_SPECIAL = /[!@#$%^&*(),.?":{}|<>]/;

export default function SignUp() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'apple' | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [showPasswordless, setShowPasswordless] = useState(true);

  // Password validation states
  const passwordValidation = {
    minLength: password.length >= PASSWORD_MIN_LENGTH,
    hasUppercase: HAS_UPPERCASE.test(password),
    hasLowercase: HAS_LOWERCASE.test(password),
    hasNumber: HAS_NUMBER.test(password),
    hasSpecial: HAS_SPECIAL.test(password),
    passwordsMatch: password === confirmPassword && password !== ''
  };

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      toast.error(t('auth.validation.allFieldsRequired'));
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error(t('auth.validation.passwordsMustMatch'));
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authService.signupWithEmail(email, password, {
        full_name: email.split('@')[0]
      });
      
      console.log('Registration response:', response);
      
      if (response.success) {
        toast.success(t('auth.signupSuccess'), {
          description: t('auth.signupSuccessDescription')
        });
        
        // Navigate to onboarding or dashboard after successful registration
        if (response.user?.id) {
          navigate('/app/onboarding');
        } else {
          // User needs email confirmation
          navigate('/auth/check-email', { 
            state: { email } 
          });
        }
      } else {
        console.log('Signup failed with error:', response.error);
        toast.error(t('auth.signupError'), {
          description: response.error
        });
      }
    } catch (error) {
      console.error('Error during registration:', error);
      toast.error(t('auth.signupError'), {
        description: error instanceof Error ? error.message : t('errors.unknownError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setSocialLoading('google');
    try {
      const response = await authService.loginWithGoogle();
      if (response.success && response.user) {
        toast.success(t('auth.success.googleSignIn'), {
          description: t('auth.success.accountCreated')
        });
        navigate('/app/dashboard');
      } else {
        toast.error(t('auth.errors.signupFailed'), {
          description: response.error || t('auth.errors.generic')
        });
      }
    } catch (error) {
      console.error('Google signup error:', error);
      const errorMessage = error instanceof Error ? error.message : 'auth.errors.generic';
      
      // Check for specific error types for better error messages
      let translationKey = 'auth.errors.generic';
      if (errorMessage.includes('popup-closed-by-user')) {
        translationKey = 'auth.errors.popupClosed';
      } else if (errorMessage.includes('popup-blocked')) {
        translationKey = 'auth.errors.popupBlocked';
      }
      
      toast.error(t('auth.errors.signupFailed'), {
        description: t(translationKey)
      });
    } finally {
      setSocialLoading(null);
    }
  };

  const handleAppleSignUp = async () => {
    setSocialLoading('apple');
    try {
      const response = await authService.loginWithApple();
      if (response.success && response.user) {
        toast.success(t('auth.success.signedIn'), {
          description: t('auth.success.accountCreated')
        });
        navigate('/app/dashboard');
      } else {
        toast.error(t('auth.errors.signupFailed'), {
          description: response.error || t('auth.errors.generic')
        });
      }
    } catch (error) {
      console.error('Apple signup error:', error);
      const errorMessage = error instanceof Error ? error.message : 'auth.errors.generic';
      
      // Check for specific error types
      let translationKey = 'auth.errors.generic';
      if (errorMessage.includes('popup-closed-by-user')) {
        translationKey = 'auth.errors.popupClosed';
      } else if (errorMessage.includes('popup-blocked')) {
        translationKey = 'auth.errors.popupBlocked';
      } else if (errorMessage.includes('account-exists-with-different-credential')) {
        translationKey = 'auth.errors.accountExistsWithDifferentCredential';
      }
      
      toast.error(t('auth.errors.signupFailed'), {
        description: t(translationKey)
      });
    } finally {
      setSocialLoading(null);
    }
  };

  const handlePasswordlessLogin = async () => {
    if (!email) {
      toast.error(t('auth.validation.emailRequired'), {
        description: t('auth.validation.emailRequiredForMagicLink')
      });
      return;
    }
    
    setLoading(true);
    try {
      const result = await authService.signInWithMagicLink(
        email, 
        `${window.location.origin}/auth/callback`
      );
      
      if (!result.success) {
        toast.error(t('auth.magicLinkError'), {
          description: result.error
        });
        return;
      }
      
      setOtpSent(true);
      toast.success(t('auth.magicLinkSent'), {
        description: t('auth.magicLinkSentDescription')
      });
    } catch (err) {
      console.error('Unexpected error with passwordless login:', err);
      toast.error(t('auth.magicLinkError'), {
        description: err instanceof Error ? err.message : t('errors.unknownError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);
  const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value);

  const ValidationItem = ({ isValid, label }: { isValid: boolean; label: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {isValid ? (
        <Check className="w-4 h-4 text-success" />
      ) : (
        <X className="w-4 h-4 text-error" />
      )}
      <span className={isValid ? 'text-success' : 'text-error'}>
        {label}
      </span>
    </div>
  );

  return (
    <div className={cn(
      "min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-background-primary dark:bg-background-dark-primary",
      language === 'ar' ? 'font-arabic' : 'font-sans'
    )}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="p-3 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
            <img 
              src={theme === 'dark' ? LogoDark : LogoLight}
              alt="Flows.Cash Logo"
              className="h-12 w-auto"
            />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
          {t('auth.createAccount')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary dark:text-text-dark-secondary">
          {t('auth.signupSubtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 py-8 px-4 shadow-sm sm:rounded-xl sm:px-10">
          {otpSent ? (
            <div className="space-y-6 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="p-3 bg-primary/10 dark:bg-primary-dark/10 rounded-full">
                  <Mail className="w-8 h-8 text-primary dark:text-primary-dark" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                  {t('auth.otp.checkEmail')}
                </h3>
                <p className="text-text-secondary dark:text-text-dark-secondary">
                  {t('auth.otp.emailSentTo')} <span className="font-medium">{email}</span>
                </p>
                <p className="text-text-secondary dark:text-text-dark-secondary">
                  {t('auth.otp.clickLink')}
                </p>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => setOtpSent(false)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-background-secondary hover:bg-background-secondary/90 text-text-primary dark:text-text-dark-primary dark:bg-background-dark-secondary dark:hover:bg-background-dark-secondary/90 border border-border dark:border-border-dark rounded-xl h-11 text-sm font-medium transition-colors"
                >
                  {t('auth.otp.tryAgain')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {showPasswordless ? (
                <div className="space-y-6">
                  <div className="p-4 bg-primary/10 dark:bg-primary-dark/10 rounded-lg border border-primary/20 dark:border-primary-dark/20">
                    <h3 className="text-base font-medium text-text-primary dark:text-text-dark-primary mb-2">
                      {t('auth.magicLink.title', 'Quick Sign Up with Magic Link')}
                    </h3>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-3">
                      {t('auth.magicLink.description', 'Get started quickly with a magic link sent to your email - no password needed.')}
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="passwordlessEmail" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          {t('auth.email.label')}
                        </label>
                        <input
                          id="passwordlessEmail"
                          type="email"
                          value={email}
                          onChange={handleEmailChange}
                          className="w-full px-3 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50 mt-1"
                          disabled={loading}
                          placeholder={t('auth.email.placeholder')}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handlePasswordlessLogin}
                        disabled={loading || !email}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl h-11 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-primary-dark dark:bg-primary-dark dark:hover:bg-primary-dark/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('auth.magicLink.sending', 'Sending Link...')}
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            {t('auth.magicLink.button', 'Email Me a Login Link')}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 mt-4">
                    <SocialLoginButton
                      icon={
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="currentColor"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                      }
                      label={t('auth.actions.continueWithGoogle')}
                      onClick={handleGoogleSignUp}
                      loading={socialLoading === 'google'}
                      disabled={!!socialLoading}
                    />
                    <SocialLoginButton
                      icon={
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M17.05 20.28c-.98.95-2.05.88-3.08.41-1.09-.47-2.06-.48-3.2 0-1.42.62-2.17.53-3.06-.38C3.33 15.85 4.14 8.27 9.15 8c1.2.08 2.05.56 2.81.57.76.01 1.74-.54 3.08-.61 2.05-.11 3.84.89 4.69 2.55-4.42 2.33-3.7 8.39.32 9.77ZM15.33 4.4c-1.78 0-3.21 1.6-3.13 3.13 1.73.15 3.21-1.55 3.13-3.13Z"
                          />
                        </svg>
                      }
                      label={t('auth.actions.continueWithApple')}
                      onClick={handleAppleSignUp}
                      loading={socialLoading === 'apple'}
                      disabled={!!socialLoading}
                    />
                  </div>
                  
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowPasswordless(false)}
                      className="text-sm text-secondary dark:text-primary-dark hover:text-secondary/80 dark:hover:text-primary-dark/80 hover:underline focus:outline-none transition-colors"
                    >
                      {t('auth.toggles.usePassword', 'Use password-based signup instead')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3">
                    <SocialLoginButton
                      icon={
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="currentColor"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                      }
                      label={t('auth.actions.continueWithGoogle')}
                      onClick={handleGoogleSignUp}
                      loading={socialLoading === 'google'}
                      disabled={!!socialLoading}
                    />
                    <SocialLoginButton
                      icon={
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M17.05 20.28c-.98.95-2.05.88-3.08.41-1.09-.47-2.06-.48-3.2 0-1.42.62-2.17.53-3.06-.38C3.33 15.85 4.14 8.27 9.15 8c1.2.08 2.05.56 2.81.57.76.01 1.74-.54 3.08-.61 2.05-.11 3.84.89 4.69 2.55-4.42 2.33-3.7 8.39.32 9.77ZM15.33 4.4c-1.78 0-3.21 1.6-3.13 3.13 1.73.15 3.21-1.55 3.13-3.13Z"
                          />
                        </svg>
                      }
                      label={t('auth.actions.continueWithApple')}
                      onClick={handleAppleSignUp}
                      loading={socialLoading === 'apple'}
                      disabled={!!socialLoading}
                    />
                    <SocialLoginButton
                      icon={<Mail className="w-5 h-5" />}
                      label={t('auth.magicLink.button', 'Email Me a Login Link')}
                      onClick={() => setShowPasswordless(true)}
                      disabled={!!socialLoading || loading}
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border dark:border-border-dark" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-secondary dark:text-text-dark-secondary">
                        {t('auth.actions.orWithEmail')}
                      </span>
                    </div>
                  </div>

                  <form className="space-y-6" onSubmit={handleSignUp}>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        {t('auth.email.label')}
                      </label>
                      <div className="mt-1">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={handleEmailChange}
                          placeholder={t('auth.email.placeholder')}
                          className="w-full px-3 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        {t('auth.password.label')}
                      </label>
                      <div className="mt-1">
                        <input
                          id="password"
                          name="password"
                          type="password"
                          autoComplete="new-password"
                          required
                          value={password}
                          onChange={handlePasswordChange}
                          placeholder={t('auth.password.placeholder')}
                          className="w-full px-3 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        {t('auth.confirmPassword.label')}
                      </label>
                      <div className="mt-1">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          autoComplete="new-password"
                          required
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          placeholder={t('auth.confirmPassword.placeholder')}
                          className="w-full px-3 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        {t('auth.password.requirements')}
                      </p>
                      <div className="grid grid-cols-1 gap-1 mt-2">
                        <ValidationItem 
                          isValid={passwordValidation.minLength} 
                          label={t('auth.password.validations.minLength', { length: PASSWORD_MIN_LENGTH })} 
                        />
                        <ValidationItem 
                          isValid={passwordValidation.hasUppercase} 
                          label={t('auth.password.validations.uppercase')} 
                        />
                        <ValidationItem 
                          isValid={passwordValidation.hasLowercase} 
                          label={t('auth.password.validations.lowercase')} 
                        />
                        <ValidationItem 
                          isValid={passwordValidation.hasNumber} 
                          label={t('auth.password.validations.number')} 
                        />
                        <ValidationItem 
                          isValid={passwordValidation.hasSpecial} 
                          label={t('auth.password.validations.special')} 
                        />
                        <ValidationItem 
                          isValid={passwordValidation.passwordsMatch} 
                          label={t('auth.password.validations.match')} 
                        />
                      </div>
                    </div>

                    <div>
                      <button
                        type="submit"
                        disabled={loading || !isPasswordValid}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl h-11 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-primary-dark dark:bg-primary-dark dark:hover:bg-primary-dark/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('auth.actions.creatingAccount')}
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            {t('auth.actions.createAccount')}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}

              <div className="text-center flex flex-col space-y-2">
                <Link
                  to="/auth/login"
                  className="text-sm text-secondary dark:text-primary-dark hover:text-secondary/80 dark:hover:text-primary-dark/80 hover:underline focus:outline-none transition-colors"
                >
                  {t('auth.toggles.alreadyHaveAccount')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}